/**
 * MetadataIQ Module API
 *
 * WHY: MetadataIQ is the schema authority for the entire platform.
 * It provides the Form Factory schemas that drive the metadata-driven
 * UI pattern mandated in SPECTREIQ_MASTER.md.
 */

import { http } from '../core/client'
import type {
  Schema,
  CreateSchemaPayload,
  Methodology,
  ProcessDefinition,
  ProcessDefinitionSummary,
  CreateProcessPayload,
  UpdateProcessPayload,
  IntegrationDefinition,
  IntegrationValidationResponse,
  CreateIntegrationPayload,
  UpdateIntegrationPayload,
} from '../core/types'

/**
 * Schema Registry API.
 * WHY: The Form Factory pattern requires fetching schemas at runtime
 * to dynamically generate forms. This avoids hardcoded UI fields.
 */
export const schemasApi = {
  /**
   * List all available schemas.
   */
  list: () => http.get<Schema[]>('/schemas', { skipMissionContext: true }),

  /**
   * Get a specific schema by ID.
   * WHY: Used by MetadataForm to fetch field definitions for dynamic
   * form generation. The schema ID follows the pattern: {module}-{entity}
   */
  get: (id: string) => http.get<Schema>(`/schemas/${id}`, { skipMissionContext: true }),

  /**
   * Validate data against a schema.
   * WHY: Client-side validation is for UX, but server-side validation
   * is the source of truth per the SSE mandate.
   */
  validate: (id: string, data: unknown) =>
    http.post<{ valid: boolean; errors?: string[] }>(`/schemas/${id}/validate`, data, { skipMissionContext: true }),

  /**
   * Create a new schema (admin only).
   */
  create: (data: CreateSchemaPayload) =>
    http.post<Schema>('/schemas', data, { skipMissionContext: true }),
}

/**
 * Methodology API.
 * WHY: JFAC methodology defines the risk calculation factors.
 * These values come from MetadataIQ rather than being hardcoded.
 */
export const methodologiesApi = {
  /**
   * Get a methodology by ID.
   * @param id - Methodology ID (default: 'jfac-2019')
   */
  get: (id: string = 'jfac-2019') =>
    http.get<Methodology>(`/methodologies/${id}`, { skipMissionContext: true }),
}

/**
 * Module Registry API.
 * WHY: The platform supports multiple modules; this endpoint
 * provides discovery of registered modules.
 */
export const modulesApi = {
  /**
   * List all registered modules.
   */
  list: () => http.get<{ module_id: string; name: string; version: string }[]>(
    '/modules',
    { skipMissionContext: true }
  ),
}

const buildQueryString = (params?: Record<string, string | number | boolean | undefined>) => {
  if (!params) return ''
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return
    search.set(key, String(value))
  })
  const query = search.toString()
  return query ? `?${query}` : ''
}

/**
 * Integration Registry API.
 */
export const integrationsApi = {
  list: (params?: { integration_type?: string; demo_only?: boolean; status?: string }) =>
    http.get<{ count: number; integrations: IntegrationDefinition[] }>(
      `/integrations${buildQueryString(params)}`,
      { skipMissionContext: true }
    ),
  get: (id: string, params?: { version?: string }) =>
    http.get<IntegrationDefinition>(
      `/integrations/${id}${buildQueryString(params)}`,
      { skipMissionContext: true }
    ),
  validate: (id: string, params?: { version?: string }) =>
    http.post<IntegrationValidationResponse>(
      `/integrations/${id}/validate${buildQueryString(params)}`,
      null,
      { skipMissionContext: true }
    ),
  create: (data: CreateIntegrationPayload) =>
    http.post<IntegrationDefinition>('/integrations', data, { skipMissionContext: true }),
  update: (id: string, data: UpdateIntegrationPayload) =>
    http.put<IntegrationDefinition>(`/integrations/${id}`, data, { skipMissionContext: true }),
}

/**
 * Process Definition API.
 * WHY: Workflow definitions are authored in MetadataIQ and drive the UI stepper.
 */
export const processesApi = {
  /**
   * List process definitions (default active only).
   */
  list: (params?: { module?: string; active_only?: boolean; status?: string }) =>
    http.get<{ count: number; processes: ProcessDefinitionSummary[] }>(
      `/processes${buildQueryString(params)}`,
      { skipMissionContext: true }
    ),

  /**
   * Get a process definition by ID.
   */
  get: (id: string, params?: { version?: string }) =>
    http.get<ProcessDefinition>(
      `/processes/${id}${buildQueryString(params)}`,
      { skipMissionContext: true }
    ),

  /**
   * Create a process definition (admin only).
   */
  create: (data: CreateProcessPayload) =>
    http.post<ProcessDefinition>('/processes', data, { skipMissionContext: true }),

  /**
   * Update a process definition (admin only).
   */
  update: (id: string, data: UpdateProcessPayload) =>
    http.put<ProcessDefinition>(`/processes/${id}`, data, { skipMissionContext: true }),

  /**
   * Publish a process definition (admin only).
   */
  publish: (id: string, params: { version: string }) =>
    http.post<ProcessDefinition>(
      `/processes/${id}/publish${buildQueryString(params)}`,
      null,
      { skipMissionContext: true }
    ),
}
